import i18n from 'i18next'
import GlobalConfig from './GlobalConfig'
import { codedErrorMessage } from './systemValues/codedErrorMessage'
import { getSessionValue } from './utilities/session_value_utils'
import { isWorkstationCovered, neverSettles, reportWorkstationCovered } from '../vision/visionSignals'

const baseEndpointUrl = GlobalConfig['back-end_address']
const isServerConnectionError = statusCode => [502, 503, 504, 401].includes(statusCode)

export const notifyServerConnectionLost = () => {
  if (window.location.pathname !== '/connection_error') {
    window.location.replace('/connection_error')
    return true
  }
  return false
}

export const apiGet = async (url, body = '') => {
  const result = await apiF(url, body, 'GET').catch((err) => {
    throw err
  })
  return result
}

export const apiGetSync = (url) => {
  const result = apiF(url, '', 'GET').catch((err) => {
    throw err
  })
  return result
}

export const apiPost = async (url, body, extraHeaders = {}, signal) => {
  const result = await apiF(url, body, 'POST', null, null, extraHeaders, signal).catch((err) => {
    throw err
  })

  return result
}

export const apiPut = async (url, body) => {
  const result = await apiF(url, body, 'PUT').catch((err) => {
    throw err
  })
  return result
}

export const apiDelete = async (url, body) => {
  const result = await apiF(url, body, 'DELETE').catch((err) => {
    throw err
  })
  return result
}

export const apiImportFile = async (url, beanParams, file) => {
  const result = await apiF(url, beanParams, 'FILE_IMPORT', file).catch((err) => {
    throw err
  })
  return result
}

export const apiFile = async (url, body, file) => {
  const result = await apiF(url, body, 'FILE', file).catch((err) => {
    throw err
  })
  return result
}
export const getBackendUrl = () => {
  return baseEndpointUrl
}

const isTokenValid = () => {
  try {
    const jwt = window.localStorage.getItem('jwt')
    if (!jwt) {
      return false
    }
    return Date.now() + 1000 * 60 < (JSON.parse(window.atob(jwt.split('.')[1]))).exp * 1000
  } catch (exception) {

  }
  return false
}

const refreshJWT = async () => {
  try {
    const options = {
      method: 'PUT',
      headers: {
        'X-MIC-SESSION': getSessionValue(),
        'Content-Type': 'application/json'
      }
    }
    const result = await fetch(baseEndpointUrl + 'token/refreshToken', options)
    const response = await result.json()
    window.localStorage.setItem('jwt', response.message)
  } catch (e) {
  }
}

export const apiPostFormData = async (url, formData) => {
  const result = await apiF(url, formData, 'POST', null, true).catch((err) => {
    throw err
  })
  return result
}

export const apiPutFormData = async (url, formData) => {
  const result = await apiF(url, formData, 'PUT', null, true).catch((err) => {
    throw err
  })
  return result
}

export const ensureValidToken = async () => {
  // Check if jwt is expired and refresh if required
  if (!isTokenValid()) {
    await refreshJWT()
  }
}

const apiF = async (url, body, method, file, formData, extraHeaders = {}, signal) => {
  await ensureValidToken()
  const options = {
    method,
    headers: {
      'X-MIC-SESSION': getSessionValue(),
      Authorization: 'Bearer ' + window.localStorage.getItem('jwt'),
      ...extraHeaders
    }
  }
  if (signal) {
    options.signal = signal
  }
  if (!formData) {
    options.headers['Content-Type'] = 'application/json'
  }
  let getOptions = ''

  if (method === 'FILE_IMPORT' || method === 'FILE') {
    const formData = new FormData()
    formData.append('file', file)
    if (method === 'FILE_IMPORT') {
      let extraParams
      if (body.extraParams) {
        extraParams = JSON.stringify(Object.fromEntries(body.extraParams))
      }
      const bean = body.bean
      formData.append('beanDtoName', bean.beanDtoName)
      formData.append('beanName', bean.beanName)
      formData.append('beanPackage', bean.beanPackage)
      formData.append('serviceName', bean.serviceName)
      formData.append('servicePackage', bean.servicePackage)
      formData.append('beanDtoPackage', bean.beanDtoPackage)
      formData.append('extraParams', extraParams)
    }
    if (method === 'FILE') {
      formData.append('order', body.order)
      formData.append('documentName', body.documentName)
      formData.append('documentType', body.documentType.id)
      formData.append('description', body.description)
      formData.append('forMosNextJobs', body.forMosNextJobs)
      if (body.jobs && body.jobs.length > 0) {
        const jobsBlob = new Blob([JSON.stringify(body.jobs)], { type: 'application/json' })
        formData.append('nextJobs', jobsBlob)
      } else {
        const emptyArrayBlob = new Blob(['[]'], { type: 'application/json' })
        formData.append('nextJobs', emptyArrayBlob)
      }
    }
    delete options.headers['Content-Type']
    options.body = formData
    options.method = 'PUT'
  } else if (method !== 'GET') {
    options.body = JSON.stringify(body)
  } else {
    getOptions = body !== '' ? '?' + new URLSearchParams(body) : body
  }
  if (formData) {
    options.body = body
  }
  let result = {}
  try {
    result = await fetch(baseEndpointUrl + url + getOptions, options)
  } catch (exception) {
    // A caller that aborted its own request has not lost the server: propagate the abort instead
    // of bouncing the whole app to /connection_error.
    if (exception?.name === 'AbortError') {
      throw exception
    }
    if (notifyServerConnectionLost()) {
      return
    }
    const error = {}
    error.status = 500
    error.name = getStatusCodeName(500)
    error.message = exception.message
    throw error
  }

  if (isServerConnectionError(result.status)) {
    if (notifyServerConnectionLost()) {
      return
    }
  }

  // Story 213 (C1 step 4): the workstation is covered by camera vigilance. The guard takes over and
  // unmounts the screen that asked, so this request never settles: no alert, no redirection (S97, S98).
  if (isWorkstationCovered(result.status)) {
    reportWorkstationCovered()
    return neverSettles()
  }

  if (isHttpStatusOk(result.status)) {
    result = await result.json().catch(() => {
      if (method === 'GET') {
        return []
      } else {
        return {}
      }
    })
    return result
  } else {
    const error = {}
    error.status = result.status
    await result.json()
      .then((data) => {
        error.name = getStatusCodeName(error.status)
        error.data = data
        if (typeof data === 'string') {
          error.message = data
        } else if (Array.isArray(data)) {
          error.message = data.join('. ')
        } else {
          // Story 204 (RN4.9, C1): a coded error is shown in the user's language; `code` and `params` travel with the
          // error, `data` stays the body as Sun sent it. Without a code, or without a key for it, exactly as before.
          // `i18n` is i18next's default instance, the one App.jsx initialises through src/i18n.js (never imported here).
          error.code = data.code
          error.params = data.params
          error.message = codedErrorMessage(i18n, data) || data.message || data.error
        }
      })
      .catch(() => {
        if (method === 'GET') {
          return []
        } else {
          return {}
        }
      })

    throw error
  }
}

export const apiGetFile = async (path) => {
  const response = await fetch(GlobalConfig['back-end_address'] + path, {
    headers: {
      'X-MIC-SESSION': getSessionValue(),
      Authorization: 'Bearer ' + window.localStorage.getItem('jwt'),
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  })
  if (isWorkstationCovered(response.status)) {
    reportWorkstationCovered()
    return neverSettles()
  }
  if (!response.ok) {
    // Historia 148 (S67): el codigo de estado viaja con el error. Sin el, un documento BORRADO desde
    // oficina (404) y un fallo de red (5xx) son indistinguibles para quien muestra el aviso. Aditivo:
    // los llamadores existentes solo leen `.message`, que no cambia.
    const error = new Error('Could not get file')
    error.status = response.status
    throw error
  }
  return response
}

export const apiPostFile = async (path, body) => {
  const response = await fetch(GlobalConfig['back-end_address'] + path, {
    method: 'POST',
    headers: {
      'X-MIC-SESSION': getSessionValue(),
      Authorization: 'Bearer ' + window.localStorage.getItem('jwt'),
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify(body)
  })

  if (isWorkstationCovered(response.status)) {
    reportWorkstationCovered()
    return neverSettles()
  }
  if (!response.ok) {
    throw new Error('Could not get file')
  }

  return response
}

export const apiDownloadFile = async (path, fileName, body = null) => {
  const response = body
    ? await apiPostFile(path, body)
    : await apiGetFile(path)

  const blob = await response.blob()
  const link = document.createElement('a')
  link.href = window.URL.createObjectURL(blob)
  link.download = fileName
  link.click()
  window.URL.revokeObjectURL(link.href)
}

export const isHttpStatusOk = statusCode => statusCode >= 200 && statusCode < 300
const getStatusCodeName = statusCode => {
  if (isHttpStatusOk(statusCode)) return 'success'
  if (statusCode >= 300 && statusCode < 400) return 'redirection'
  if (statusCode >= 400 && statusCode < 500) return 'warning'
  if (statusCode >= 500) return 'error'
  return undefined
}

export const apiGetPdf = async (url) => {
  const response = await fetch(baseEndpointUrl + url, {
    method: 'GET',
    headers: {
      'X-MIC-SESSION': getSessionValue(),
      Authorization: 'Bearer ' + window.localStorage.getItem('jwt'),
      'Content-Type': 'application/json'
    }
  })
  if (isWorkstationCovered(response.status)) {
    reportWorkstationCovered()
    return neverSettles()
  }
  return response.blob()
}
